<template>
  <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
    <b-form-group label="Interviews Category" label-for="simulation_purpose" label-class="form-label">
      <v-select
        id="simulation_purpose"
        class="form-control v-select-custom"
        label="text"
        v-model="simulation.simulation_purpose"
        :reduce="simulation_purpose => simulation_purpose.value"
        placeholder="Select Interviews Category"
        :options="purposeOptions"
        :clearable="false"
      >
        <template #search="{ attributes, events }">
          <input class="vs__search" v-bind="attributes" v-on="events" />
        </template>
        <template slot="option" slot-scope="option">
          <span>{{ option.text }}</span>
        </template>
        <template slot="selected-option" slot-scope="option">
          {{ option.text }}
        </template>
      </v-select>
    </b-form-group>
    <b-form-group label="Interviews Type" label-for="simulation_type" label-class="form-label">
      <div v-for="option in typeOptions" :key="option.value">
        <b-form-radio class="my-2" v-model="simulation.simulation_type" :value="option.value">
          <span>{{ option.title }}: </span>
          <span style="font-size: 14px; color: grey">{{ option.description }}</span>
        </b-form-radio>
      </div>

      <b-form-invalid-feedback
        :state="!$v.simulation.simulation_type.required && $v.simulation.simulation_type.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>

    <b-form-group label="Interview Provider" label-for="provider" label-class="form-label">
      <div class="row px-3">
        <div v-for="option in SIMULATION_PROVIDERS" :key="option.value" class="mr-3">
          <b-form-radio v-model="simulation.provider" :value="option.value" :disabled="isEditing">
            <div class="mr-1">{{ option.name }}</div>
          </b-form-radio>
        </div>
      </div>
      <b-form-text v-if="isEditing">
        The provider is fixed once the interview has been created.
      </b-form-text>
    </b-form-group>

    <b-form-group label="Image" label-for="image" label-class="form-label" class="row-align-items-center">
      <b-media class="align-items-center" vertical-align="center">
        <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
          <span v-if="isImgUploading">...</span>
          <b-img :src="simulation.image" class="img-fluid" width="40" alt="Image" v-else-if="simulation.image" />
          <i class="fas fa-shopping-bag fa-lg" v-else></i>
        </fmv-avatar>

        <image-uploader
          @image="e => setImage(e, 'image')"
          :isRequired="!simulation.image"
          :isUploading="isImgUploading"
          placeholder="Select Image"
        />
      </b-media>
      <b-form-invalid-feedback :state="!$v.simulation.image.required && $v.simulation.image.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>

    <b-form-group label="Title" label-for="title" label-class="form-label">
      <b-form-input
        id="title"
        placeholder="Enter interview's title."
        v-model.trim="simulation.title"
        :maxlength="250"
        required
        :state="
          ($v.simulation.title.required && $v.simulation.title.maxLength) || !$v.simulation.title.$dirty ? null : false
        "
      />
      <b-form-invalid-feedback v-if="!$v.simulation.title.required">
        This field is required.
      </b-form-invalid-feedback>
      <b-form-invalid-feedback v-if="!$v.simulation.title.maxLength">
        Title must be at most 250 characters long.
      </b-form-invalid-feedback>
    </b-form-group>

    <b-form-group
      v-if="getLoggedInUser.role === USER_ROLES.SUPER_ADMIN"
      label="Video URL"
      label-for="video_url"
      label-class="form-label"
    >
      <b-form-input
        id="video_url"
        placeholder="Enter interview's video url."
        v-model.trim="simulation.video_url"
        :maxlength="250"
        required
      />
    </b-form-group>

    <b-form-group v-if="getLoggedInUser.role === USER_ROLES.SUPER_ADMIN" label-class="form-label">
      <b-form-checkbox v-model="simulation.is_featured" name="check-button" switch>
        Is Featured?
      </b-form-checkbox>
    </b-form-group>

    <b-form-group
      :label="isSchoolScenario ? 'Student instructions (shown to the student, never to the avatar)' : 'Instructions'"
      label-for="instructions"
      label-class="form-label"
    >
      <b-form-textarea
        id="questions"
        :placeholder="
          isSchoolScenario
            ? 'The student\'s role, their goal, what to accomplish, and what not to promise.'
            : 'Enter simulation\'s instructions.'
        "
        :rows="5"
        v-model="simulation.instructions"
        required
        :state="!$v.simulation.instructions.required && $v.simulation.instructions.$dirty ? false : null"
      />
      <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
    </b-form-group>

    <b-form-group
      :label="isSchoolScenario ? 'Student notes (shown beside the call)' : 'Details'"
      label-for="details"
      label-class="form-label"
    >
      <editor
        :init="{
          height: 400,
          menubar: 'edit view insert format tools table help',
          plugins: [
            'advlist autolink lists link image charmap print preview anchor',
            'searchreplace visualblocks code fullscreen',
            'insertdatetime media table paste code help wordcount',
          ],
          toolbar:
            'undo redo | formatselect | bold italic backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | removeformat | help',
        }"
        v-model="simulation.details"
        placeholder="Enter simulation's details."
      />
      <b-form-invalid-feedback :state="!$v.simulation.details.required && $v.simulation.details.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>
  </b-form>
</template>

<script>
import axios from 'axios';
import { pick, head } from 'lodash';
import Editor from '@tinymce/tinymce-vue';
import { required, maxLength } from 'vuelidate/lib/validators';
import { mapGetters, mapActions } from 'vuex';
import ImageUploader from '@/components/ImageUploader.vue';
import { FmvAvatar } from 'fmv-avatar';
import { SIMULATION_PROVIDERS, USER_ROLES } from '../../../common/constants';

export default {
  name: 'JobBasicInfoForm',
  components: { Editor, ImageUploader, FmvAvatar },

  props: {
    simulationData: { type: Object, required: true },
  },

  data() {
    return {
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      uploadFile: { image: null },
      uploadPercent: { image: 0 },
      uploadCancelTokenSource: { image: null },

      isImgUploading: false,
      options: [
        {
          title: `Interview Type
General Professional Competency`,
          value: 'general_professional',
          description: `A broader assessment of overall professional demeanor, work ethic, general knowledge,
and soft skills.`,
        },
        // TODO: add in backend
        // {
        //   title: `Basic Care and Procedures`,
        //   value: 'basic_care_and_procedures',
        //   description: `Focuses on skills in routine patient care and basic medical procedures.`
        // },
        {
          title: `Behavioral and Situational`,
          value: 'behavioral_and_situational',
          description: `Assesses responses to common workplace situations and behavioral patterns.`,
        },
        {
          title: `Communication Skills`,
          value: 'communication_skills',
          description: `Evaluates effective communication with patients, families, and team members.`,
        },
        {
          title: `Teamwork and Collaboration`,
          value: 'teamwork_and_collaboration',
          description: `Focuses on the ability to work as part of a healthcare team.`,
        },
      ],

      simulation: {
        simulation_purpose: 'employment',
        simulation_type: '',
        title: '',
        image: '',
        instructions: '',
        details: '',
        is_featured: false,
        video_url: '',
        provider: 'retell',
      },
      SIMULATION_PROVIDERS,
      USER_ROLES,
      simulation_purpose_options: [
        { text: 'Employment', value: 'employment' },
        { text: 'School Admission', value: 'school_admission' },
        { text: 'School Scenario', value: 'school_scenario', superAdminOnly: true },
      ],
    };
  },

  validations() {
    return {
      simulation: {
        image: { required },
        title: { required, maxLength: maxLength(250) },
        instructions: { required },
        simulation_type: { required },
        details: { required },
        simulation_purpose: { required },
        provider: { required },
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    typeOptions() {
      return this.options;
    },
    isSchoolScenario() {
      return this.simulation.simulation_purpose === 'school_scenario';
    },
    purposeOptions() {
      // School scenarios are authored by super admins only.
      if (this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN) return this.simulation_purpose_options;
      return this.simulation_purpose_options.filter(option => !option.superAdminOnly);
    },
    // The provider is baked into the agent created with the interview, so
    // switching it on an existing one would orphan that agent.
    isEditing() {
      return Boolean(this.$route.params.id);
    },
  },

  watch: {
    simulationData: {
      handler() {
        this.simulation = pick(this.simulationData, [
          'simulation_purpose',
          'simulation_type',
          'title',
          'instructions',
          'image',
          'details',
          'is_featured',
          'video_url',
          'provider',
        ]);
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('simulations', ['getSimulationImageUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    head,
    validateFormData() {
      this.$v.$touch();
      return {
        isValid: !this.$v.$invalid,
        formData: { ...this.simulation },
      };
    },

    setImage(file, fileType) {
      this.uploadFile[fileType] = file;
      this.uploadImage(fileType);
    },
    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isImgUploading = uploadType === 'image';

      try {
        const urlResp = await this.getSimulationImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type,
          upload_type: 'images',
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'image') {
          this.simulation.image = urlResp.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'image') this.isImgUploading = false;
    },

    updateJobData(data) {
      this.simulation = pick(data, [
        'job_type',
        'title',
        'description',
        'is_active',
        'type',
        'schedule',
        'application_link',
        'minimum_pay',
        'maximum_pay',
        'pay_rate',
        'no_of_hires',
        'schools',
        'address',
        'city',
        'state',
        'zipcode',
        'country',
        // 'simulation_purpose',
      ]);
    },
  },

  async mounted() {},
};
</script>
